import type { DashboardSummary } from '../types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import BackupNowModal from '@features/backups/components/BackupNowModal';
import { useDashboardSummary } from '../hooks/useDashboardSummary';
import SimpleDashboard from '../components/SimpleDashboard';
import AdvancedDashboard from '../components/AdvancedDashboard';
import { OnboardingWizard, useOnboarding } from '@features/onboarding';
import { KpiCard } from '@shared/components/KpiCard';
import { getStatusTone } from '@shared/config/theme';

export default function Dashboard() {
  const { t } = useTranslation('dashboard');
  const [timePeriod, setTimePeriod] = useState('30d');
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [backupNowModalOpen, setBackupNowModalOpen] = useState(false);
  const [viewSaving, setViewSaving] = useState(false);
  const [viewSaveError, setViewSaveError] = useState(false);

  const { dashboardData, loading: dataLoading, setDashboardView } = useDashboardSummary(timePeriod);
  const {
    shouldShowOnboarding,
    isLoading: onboardingLoading,
    skipOnboarding,
    completeOnboarding,
  } = useOnboarding();

  // Show onboarding wizard if needed
  if (!onboardingLoading && shouldShowOnboarding) {
    return <OnboardingWizard onComplete={completeOnboarding} onSkip={skipOnboarding} />;
  }

  return (
    <>
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="app-page-title">{t('title')}</h1>
          <p className="app-page-subtitle">{t('subtitle')}</p>
        </div>

        {dashboardData && (
          <div className="self-start md:self-auto">
            <div
              aria-busy={viewSaving}
              className="flex bg-surface-2-light dark:bg-surface-2-dark p-1.5 rounded-xl shadow-inner border dark:border-surface-border-dark"
            >
              <ViewToggle
                active={dashboardData.dashboard_view === 'SIMPLE'}
                disabled={viewSaving}
                label={t('view.overview')}
                onClick={() => handleSwitchView('SIMPLE')}
              />
              <ViewToggle
                active={dashboardData.dashboard_view === 'ADVANCED'}
                disabled={viewSaving}
                label={t('view.analytics')}
                onClick={() => handleSwitchView('ADVANCED')}
              />
            </div>
            {viewSaving && (
              <p
                role="status"
                className="mt-2 text-xs text-text-subtle-light dark:text-text-subtle-dark"
              >
                {t('view.saving')}
              </p>
            )}
            {viewSaveError && (
              <p
                role="alert"
                className="mt-2 text-xs text-state-danger-light dark:text-state-danger-dark"
              >
                {t('view.saveError')}
              </p>
            )}
          </div>
        )}
      </div>

      {dataLoading ? (
        <div className="py-20 flex flex-col items-center justify-center space-y-4">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600"></div>
          <p className="text-text-subtle-light dark:text-text-subtle-dark font-bold uppercase tracking-widest text-[10px]">
            {t('loading')}
          </p>
        </div>
      ) : dashboardData ? (
        <>
          <KpiGrid dashboardData={dashboardData} />
          {dashboardData.dashboard_view === 'ADVANCED' ? (
            <AdvancedDashboard
              dashboardData={dashboardData}
              setBackupNowModalOpen={setBackupNowModalOpen}
              timePeriod={timePeriod}
              setTimePeriod={setTimePeriod}
              selectedAgentId={selectedAgentId}
              setSelectedAgentId={setSelectedAgentId}
            />
          ) : (
            <SimpleDashboard
              dashboardData={dashboardData}
              setBackupNowModalOpen={setBackupNowModalOpen}
            />
          )}
        </>
      ) : (
        <div className="bg-surface-card-light dark:bg-surface-card-dark rounded-2xl border-2 border-dashed border-surface-border-light dark:border-surface-border-dark py-20 text-center">
          <p className="text-text-subtle-light dark:text-text-subtle-dark font-medium">
            {t('error')}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 text-brand-600 dark:text-brand-400 font-bold hover:underline"
          >
            {t('retry')}
          </button>
        </div>
      )}

      <BackupNowModal isOpen={backupNowModalOpen} onClose={() => setBackupNowModalOpen(false)} />
    </>
  );

  async function handleSwitchView(newView: string) {
    if (viewSaving || newView === dashboardData?.dashboard_view) return;

    setViewSaving(true);
    setViewSaveError(false);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/v1/dashboard/view', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ dashboard_view: newView }),
      });
      if (!response.ok) throw new Error('Failed to save dashboard view');
      setDashboardView(newView);
    } catch {
      setViewSaveError(true);
    } finally {
      setViewSaving(false);
    }
  }
}

/**
 * 4-cell KPI grid from the approved hybrid layout (`.kpis` in
 * qa-screenshots/layout-hybrid.html). Every value comes from the payload the
 * hook already loaded — no extra request, no new field.
 */
function KpiGrid({ dashboardData }: { dashboardData: DashboardSummary }) {
  const { t, i18n } = useTranslation('dashboard');
  const { system_health, storage_usage, backup_status, backup_configs_count } = dashboardData;

  const agentsOffline = system_health.agents_total - system_health.agents_online;
  const agentsTone = getStatusTone(system_health.status);

  const storageTone: 'ok' | 'warn' = storage_usage.usage_percent > 70 ? 'warn' : 'ok';

  const lastBackupTone = getStatusTone(backup_status.last_backup_status);
  const lastBackupLabel = !backup_status.last_backup_time
    ? t('lastBackup.noBackups')
    : lastBackupTone === 'ok'
      ? t('lastBackup.success')
      : lastBackupTone === 'danger'
        ? t('lastBackup.failed')
        : lastBackupTone === 'info'
          ? t('status.inProgress', 'Em andamento')
          : t('lastBackup.unknown');

  return (
    <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <KpiCard
        label={t('analytics.agentsOnline')}
        value={`${system_health.agents_online}/${system_health.agents_total}`}
        meta={
          agentsOffline > 0
            ? `${agentsOffline} ${t(agentsOffline === 1 ? 'status.offline' : 'status.offlinePlural')}`
            : t('statusCards.healthy')
        }
        deltaTone={agentsTone === 'ok' ? 'ok' : 'warn'}
      />
      <KpiCard
        label={t('stats.storageUsed')}
        value={
          storage_usage.limit_gb > 0
            ? `${storage_usage.usage_percent.toLocaleString(i18n.resolvedLanguage, { maximumFractionDigits: 0 })}%`
            : '—'
        }
        meta={
          storage_usage.limit_gb > 0
            ? `${storage_usage.used_gb.toLocaleString(i18n.resolvedLanguage, { maximumFractionDigits: 1 })} / ${storage_usage.limit_gb.toLocaleString(i18n.resolvedLanguage, { maximumFractionDigits: 1 })} GB`
            : `${storage_usage.used_gb.toLocaleString(i18n.resolvedLanguage, { maximumFractionDigits: 1 })} GB · ${t('statusCards.limitNotConfigured')}`
        }
        deltaTone={storageTone}
      />
      <KpiCard
        label={t('lastBackup.title')}
        value={
          backup_status.last_backup_time
            ? new Date(backup_status.last_backup_time).toLocaleTimeString(i18n.resolvedLanguage, {
                hour: '2-digit',
                minute: '2-digit',
              })
            : '—'
        }
        meta={lastBackupLabel}
        deltaTone={
          lastBackupTone === 'ok' ? 'ok' : lastBackupTone === 'danger' ? 'warn' : 'neutral'
        }
      />
      <KpiCard
        label={t('stats.totalBackups')}
        value={storage_usage.backup_count.toLocaleString(i18n.resolvedLanguage)}
        meta={t('statusCards.activeBackups', { count: backup_configs_count })}
      />
    </div>
  );
}

function ViewToggle({
  active,
  disabled,
  label,
  onClick,
}: {
  active: boolean;
  disabled: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={`min-h-11 px-3 sm:px-4 py-2 text-xs font-semibold rounded-lg transition-colors disabled:cursor-wait disabled:opacity-60 ${
        active
          ? 'bg-surface-card-light dark:bg-surface-card-dark text-brand-600 dark:text-brand-400 shadow-sm'
          : 'text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-600 dark:hover:text-slate-300'
      }`}
    >
      {label}
    </button>
  );
}
